<template>
  <div
    ref="containerRef"
    class="relative flex h-full w-full cursor-crosshair items-center justify-center overflow-hidden"
    :class="backgroundClass"
    :style="{ padding: isDesktop ? `60px 16px ${isFeedbackVisible ? ' 140px' : ''}` : '10px' }"
    data-testid="pinning-artboard-container"
    @click="handleContainerClick"
  >
    <img
      ref="imageRef"
      :src="imageUrl"
      class="pointer-events-none block max-h-full max-w-full select-none object-contain"
      alt="Artwork"
      draggable="false"
      data-testid="pinning-artboard-image"
      :style="{ transform: `scale(${zoom})`, transformOrigin: 'center center' }"
      @load="onImageLoad"
    />

    <!-- Overlay sized and positioned to match the rendered image -->
    <div
      v-if="imageRect.width > 0"
      class="pointer-events-none absolute"
      :style="{
        width: `${imageRect.width}px`,
        height: `${imageRect.height}px`,
        left: `${imageRect.left}px`,
        top: `${imageRect.top}px`,
        transform: `scale(${zoom})`,
        transformOrigin: 'center center'
      }"
    >
      <!-- Areas of interest overlays (not gated by showPins) -->
      <AreasOfInterestOverlay
        v-for="pin in pinsWithAreasOfInterest"
        :key="`aoi-${pin.id}`"
        :areas-of-interest="pin.areasOfInterest"
        :natural-width="naturalWidth"
        :natural-height="naturalHeight"
        :is-visible="isOverlayVisible(pin)"
        :pin-id="pin.id"
        :segmentation-mask-url="pin.segmentationMaskUrl"
      />

      <!-- Pin markers -->
      <template v-if="showPins">
        <div
          v-for="pin in sortedPins"
          :key="pin.id"
          :class="[
            'pointer-events-auto absolute z-[5] flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-pointer select-none items-center justify-center rounded-full border-[1.5px] border-white text-[11px] font-bold leading-none text-white transition-opacity duration-150',
            'shadow-[0_1px_4px_rgba(0,0,0,0.25)]',
            pin.id === activePinId && 'z-10',
            activePinId && pin.id !== activePinId && 'opacity-40',
            pin.state === 'processing' && 'animate-pulse'
          ]"
          :style="{
            left: `${(pin.coordinates.x / naturalWidth) * 100}%`,
            top: `${(pin.coordinates.y / naturalHeight) * 100}%`,
            backgroundColor: PIN_COLORS[pin.originalIndex % PIN_COLORS.length]
          }"
          :data-testid="`pinning-artboard-pin-${pin.originalIndex + 1}`"
          @click.stop="emit('pin-click', pin.id)"
          @mouseenter="emit('pin-hover', pin.id)"
          @mouseleave="emit('pin-unhover')"
        >
          {{ pin.originalIndex + 1 }}
        </div>
      </template>
    </div>

    <div
      v-if="showTooltip"
      class="absolute left-1/2 top-[40%] z-[15] -translate-x-1/2 -translate-y-full"
      data-testid="pinning-artboard-tooltip"
      @click.stop
    >
      <div
        class="relative flex max-w-[260px] items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2.5 text-center text-sm text-white shadow-[0_4px_12px_rgba(0,0,0,0.2)]"
      >
        <span>{{ tooltipMessage }}</span>
        <button
          type="button"
          class="cursor-pointer border-none bg-transparent p-0 text-lg leading-none text-white opacity-80 hover:opacity-100"
          @click.stop="emit('tooltip-dismiss')"
        >
          &times;
        </button>
      </div>
      <!-- Arrow -->
      <div class="flex justify-center">
        <div class="h-0 w-0 border-x-[8px] border-t-[8px] border-x-transparent border-t-blue-600" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useDtfStore } from "../store";
import AreasOfInterestOverlay from "./AreasOfInterestOverlay.vue";
import { PIN_COLORS } from "./composables/usePinningPanel";

defineOptions({ name: "PinningArtboard" });

const props = defineProps({
  imageUrl: { type: String, required: true },
  pins: { type: Array, default: () => [] },
  activePinId: { type: String, default: null },
  hoveredPinId: { type: String, default: null },
  showPins: { type: Boolean, default: true },
  canAddPin: { type: Boolean, default: true },
  zoom: { type: Number, default: 1 },
  showTooltip: { type: Boolean, default: false },
  tooltipMessage: { type: String, default: "" },
  isDesktop: { type: Boolean, default: false },
  isFeedbackVisible: { type: Boolean, default: false }
});

// Sort pins so the active pin renders last (on top), preserving original index for color/numbering
const sortedPins = computed(() =>
  props.pins
    .map((pin, index) => ({ ...pin, originalIndex: index }))
    .sort((a, b) => {
      if (a.id === props.activePinId) return 1;
      if (b.id === props.activePinId) return -1;
      return 0;
    })
);

const emit = defineEmits(["add-pin", "pin-click", "pin-hover", "pin-unhover", "tooltip-dismiss"]);

const store = useDtfStore();

const backgroundClass = computed(() => {
  if (store.backgroundType === "green") {
    return "pinning-artboard--green";
  }
  return "pinning-artboard--checkered";
});

const containerRef = ref(null);
const imageRef = ref(null);
const imageLoaded = ref(false);
const imageRect = reactive({ width: 0, height: 0, left: 0, top: 0 });
const naturalWidth = ref(0);
const naturalHeight = ref(0);

const isHoveringCompletedPin = computed(() => {
  if (!props.hoveredPinId) return false;
  const hoveredPin = props.pins.find(p => p.id === props.hoveredPinId);
  return hoveredPin?.state === "ready" || hoveredPin?.state === "unsupported";
});

const pinsWithAreasOfInterest = computed(() =>
  props.pins.filter(
    p => p.areasOfInterest && p.areasOfInterest.objects && p.areasOfInterest.objects.length > 0
  )
);

function isOverlayVisible(pin) {
  const isCompleted = pin.state === "ready" || pin.state === "unsupported";
  if (isCompleted) {
    return props.hoveredPinId === pin.id;
  }
  return pin.highlightAreasEnabled !== false && !isHoveringCompletedPin.value;
}

let resizeObserver = null;

function updateImageRect() {
  const img = imageRef.value;
  const container = containerRef.value;
  if (!img || !container || !imageLoaded.value) return;

  const containerRect = container.getBoundingClientRect();
  const imgRect = img.getBoundingClientRect();
  const zoom = props.zoom > 0 ? props.zoom : 1;
  const baseWidth = imgRect.width / zoom;
  const baseHeight = imgRect.height / zoom;
  const widthDelta = imgRect.width - baseWidth;
  const heightDelta = imgRect.height - baseHeight;

  imageRect.width = baseWidth;
  imageRect.height = baseHeight;
  imageRect.left = imgRect.left - containerRect.left + widthDelta / 2;
  imageRect.top = imgRect.top - containerRect.top + heightDelta / 2;
}

function onImageLoad() {
  imageLoaded.value = true;
  const img = imageRef.value;
  if (img) {
    naturalWidth.value = img.naturalWidth;
    naturalHeight.value = img.naturalHeight;
  }
  updateImageRect();
}

function handleContainerClick(e) {
  if (!props.canAddPin || !imageLoaded.value) return;

  const container = containerRef.value;
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const zoom = props.zoom > 0 ? props.zoom : 1;
  const baseWidth = imageRect.width;
  const baseHeight = imageRect.height;
  if (baseWidth <= 0 || baseHeight <= 0) return;

  const displayWidth = baseWidth * zoom;
  const displayHeight = baseHeight * zoom;
  if (displayWidth <= 0 || displayHeight <= 0) return;

  const displayLeft = imageRect.left - (displayWidth - baseWidth) / 2;
  const displayTop = imageRect.top - (displayHeight - baseHeight) / 2;
  const relX = (e.clientX - containerRect.left - displayLeft) / displayWidth;
  const relY = (e.clientY - containerRect.top - displayTop) / displayHeight;

  if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return;

  const nw = naturalWidth.value;
  const nh = naturalHeight.value;
  if (nw <= 0 || nh <= 0) return;
  const x = Math.min(Math.max(Math.round(relX * nw), 0), Math.max(nw - 1, 0));
  const y = Math.min(Math.max(Math.round(relY * nh), 0), Math.max(nh - 1, 0));

  emit("add-pin", { x, y });
}

onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      updateImageRect();
    });
    resizeObserver.observe(containerRef.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";

.pinning-artboard {
  &--checkered {
    background: $transparent-bg;
    background-color: #fff;
  }

  &--green {
    background-color: #04f404;
  }
}
</style>
